<!--
  Search box + filters for the media library. Text search matches whole words in item notes
  and tags, and set / progression names; end a word with * for a prefix (bruis*). The scope picks this
  patient, all the caregiver's patients, or the personal library. Dates are a recent range or a
  custom From / To; the diagnosis list is the open patient's own, so it hides across patients.
-->
<template>
  <div class="md-filterbar">
    <div class="md-filterbar-search">
      <input class="md-input" type="search" :value="modelValue.text" placeholder="Search media (e.g. bruise, wound, bruis*)" aria-label="Search media"
        @input="patch({ text: $event.target.value })" />
      <div v-if="modelValue.text && showScope" class="md-seg md-filterbar-scope">
        <button :class="{ on: modelValue.scope === 'patient' }" :disabled="store.inLibrary" @click="patch({ scope: 'patient' })">This patient</button>
        <button v-if="store.canManage" :class="{ on: modelValue.scope === 'all' }" @click="patch({ scope: 'all' })">All my patients</button>
        <button v-if="store.canManage" :class="{ on: modelValue.scope === 'mine' }" @click="patch({ scope: 'mine' })">My library</button>
      </div>
    </div>
    <div v-if="showKinds" class="md-chips">
      <button v-for="k in kinds" :key="k.key" class="md-chip" :class="{ on: modelValue.kind === k.key }" @click="patch({ kind: k.key })">
        {{ k.label }}<span v-if="counts && counts[k.key] !== undefined" class="n">({{ counts[k.key] }})</span>
      </button>
    </div>
    <div v-if="showSelects" class="md-filterbar-selects">
      <select class="md-select" :value="modelValue.date" aria-label="Date" @change="patch({ date: $event.target.value })">
        <option value="">All dates</option>
        <option value="7">Last 7 days</option>
        <option value="30">Last 30 days</option>
        <option value="90">Last 90 days</option>
        <option value="custom">Custom range…</option>
      </select>
      <template v-if="modelValue.date === 'custom'">
        <input class="md-input md-filterbar-date" type="date" :value="modelValue.date_from" :max="modelValue.date_to || undefined" aria-label="From"
          @change="patch({ date_from: $event.target.value })" />
        <span class="md-small md-muted">to</span>
        <input class="md-input md-filterbar-date" type="date" :value="modelValue.date_to" :min="modelValue.date_from || undefined" aria-label="To"
          @change="patch({ date_to: $event.target.value })" />
      </template>
      <select class="md-select" :value="modelValue.primary_tag" aria-label="Tag" @change="patch({ primary_tag: $event.target.value })">
        <option value="">All tags</option>
        <option v-for="t in store.config.primary_tags" :key="t.code" :value="t.code">{{ t.label }}</option>
      </select>
      <select v-if="showDiagnoses" class="md-select" :value="modelValue.orddiagid" aria-label="Diagnosis" @change="patch({ orddiagid: $event.target.value })">
        <option value="">All diagnoses</option>
        <option v-for="d in diagnoses" :key="d.orddiagid" :value="String(d.orddiagid)">{{ d.diagnosis_code }} · {{ d.label }}</option>
      </select>
      <select class="md-select" :value="modelValue.author" aria-label="Author" @change="patch({ author: $event.target.value })">
        <option value="">All authors</option>
        <option v-for="(n, uid) in store.config.people" :key="uid" :value="uid">{{ n }}</option>
      </select>
      <select class="md-select" :value="modelValue.sort" aria-label="Sort" @change="patch({ sort: $event.target.value })">
        <option value="newest">Newest</option>
        <option value="oldest">Oldest</option>
        <option v-if="modelValue.text" value="diagnosis">Diagnosis</option>
        <option v-if="modelValue.text && modelValue.scope === 'all'" value="patient">Patient</option>
      </select>
      <button v-if="active" class="md-link md-small" @click="clear">Clear</button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useMediaStore } from '@/stores/mediaStore';
import { listPatientDiagnoses } from '@/api/mediaApi';

const props = defineProps({
  modelValue: { type: Object, required: true },
  counts: { type: Object, default: null },
  showKinds: { type: Boolean, default: true },
  showScope: { type: Boolean, default: true },
  // The "Notes & messages" tab, for a patient (the personal library has no notes).
  showNotes: { type: Boolean, default: false },
  showSelects: { type: Boolean, default: true },
});
const emit = defineEmits(['update:modelValue']);
const store = useMediaStore();
const KINDS = [
  { key: 'all', label: 'All' }, { key: 'image', label: 'Images' }, { key: 'video', label: 'Videos' },
  { key: 'set', label: 'Sets' }, { key: 'progression', label: 'Progressions' },
];
const kinds = computed(() => (props.showNotes ? [...KINDS, { key: 'notes', label: 'Notes & messages' }] : KINDS));
const active = computed(() => !!(props.modelValue.text || props.modelValue.date || props.modelValue.primary_tag || props.modelValue.author || props.modelValue.orddiagid));
function patch(f) { emit('update:modelValue', { ...props.modelValue, ...f }); }
function clear() { patch({ text: '', date: '', date_from: '', date_to: '', primary_tag: '', author: '', orddiagid: '', scope: 'patient' }); }

const diagnoses = ref([]);
const showDiagnoses = computed(() => !store.inLibrary && diagnoses.value.length > 0 && !(props.modelValue.text && props.modelValue.scope !== 'patient'));
watch(() => store.patientUid, async (uid) => {
  diagnoses.value = uid ? await listPatientDiagnoses(uid) : [];
}, { immediate: true });
</script>

<style scoped>
.md-filterbar { display:flex; flex-direction:column; gap:10px; margin-bottom:16px; }
.md-filterbar-search { display:flex; gap:8px; flex-wrap:wrap; }
.md-filterbar-search .md-input { flex:1 1 260px; }
.md-filterbar-scope button:disabled { opacity:.45; cursor:not-allowed; }
.md-filterbar-selects { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
.md-filterbar-selects .md-select { width:auto; flex:0 1 auto; padding:6px 8px; font-size:14px; }
.md-filterbar-selects .md-filterbar-date { width:auto; flex:0 1 150px; padding:5px 8px; font-size:14px; }
</style>
